// src/pages/admin/AdminQuizReview.js
//
// The review queue for educator-submitted quiz questions.
//
// ===========================================================================
// WHY THIS SCREEN IS THE POINT OF THE WHOLE FEATURE
// ===========================================================================
// Collecting educator questions is easy. Knowing which ones are GOOD is the
// hard part, and it's where the value actually is.
//
// You already record question-by-question answer detail
// (game.latestDetail.answers, written by UniversalQuizGame). That means every
// submitted question arrives here with evidence attached: how many students
// answered it and what share got it right.
//
// That number is the reviewing signal. A question 95% of students get right
// is probably too easy to be worth much. A question 12% get right is either
// excellent or broken — and you can tell which in about four seconds by
// reading it, which you cannot do from the text alone. Curating on evidence
// rather than taste is the difference between a question bank and a pile.
//
// Promotion writes to `quizzes/{slug}`, the global library. That is a
// deliberate, admin-only, one-at-a-time action — never automatic. Anything
// promoted here is served to children in every school.

import React, { useState, useEffect, useCallback } from "react";
import {
  getFirestore,
  collectionGroup,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

const titleCase = (s = "") =>
  String(s).replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function AdminQuizReview() {
  const db = getFirestore();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // -------------------------------------------------------------------------
  // Load submissions, then attach performance data for each one
  // -------------------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const snap = await getDocs(
        query(
          collectionGroup(db, "topics"),
          where("status", "==", "submitted")
        )
      );

      const rows = snap.docs.map((d) => ({
        path: d.ref.path,
        educatorCode: d.ref.parent.parent?.id || "",
        ...d.data(),
      }));

      // Attach per-question stats from the classroom mirror. Best-effort: a
      // submission with no plays yet is still reviewable, just without
      // evidence.
      const withStats = await Promise.all(
        rows.map(async (row) => {
          try {
            const stats = await gatherStats(db, row.educatorCode, row.slug);
            return { ...row, stats };
          } catch {
            return { ...row, stats: null };
          }
        })
      );

      setSubmissions(withStats);
    } catch (err) {
      console.error("[AdminQuizReview] load failed:", err);
      setError(
        "Couldn't load submissions. Check that the collectionGroup index on `topics` exists."
      );
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    load();
  }, [load]);

  // -------------------------------------------------------------------------

  const setStatus = async (row, status) => {
    setBusySlug(row.path);
    setMessage("");
    setError("");
    try {
      await setDoc(
        doc(db, "educatorQuizzes", row.educatorCode, "topics", row.slug),
        { status, reviewedAt: serverTimestamp() },
        { merge: true }
      );
      setSubmissions((prev) => prev.filter((r) => r.path !== row.path));
      setMessage(
        status === "rejected"
          ? `Declined "${titleCase(row.slug)}". The educator's own class keeps using it.`
          : `Updated "${titleCase(row.slug)}".`
      );
    } catch (err) {
      console.error(err);
      setError("Couldn't update that submission.");
    } finally {
      setBusySlug("");
    }
  };

  /**
   * Promote into the global library.
   *
   * MERGES rather than replaces. A slug that already has canonical questions
   * gets the educator's appended, not swapped — replacing would silently
   * discard your own content, and there's no undo.
   */
  const promote = async (row) => {
    setBusySlug(row.path);
    setMessage("");
    setError("");

    try {
      const globalRef = doc(db, "quizzes", row.slug);
      const existing = await getDoc(globalRef);
      const current = existing.exists() ? existing.data() : {};
      const currentQuestions = Array.isArray(current.questions)
        ? current.questions
        : [];

      // Don't duplicate a question that's already there.
      const seen = new Set(
        currentQuestions.map((q) =>
          String(q.question || "").trim().toLowerCase()
        )
      );

      const incoming = (row.questions || [])
        .filter((q) => !seen.has(String(q.question || "").trim().toLowerCase()))
        .map((q) => ({
          ...q,
          contributedBy: row.educatorName || "Educator",
          contributedCode: row.educatorCode,
        }));

      if (incoming.length === 0) {
        setError("Every question in this submission is already in the library.");
        setBusySlug("");
        return;
      }

      await setDoc(
        globalRef,
        {
          title: current.title || row.title || titleCase(row.slug),
          gameType: current.gameType || row.gameType || "multipleChoice",
          questions: [...currentQuestions, ...incoming],
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "educatorQuizzes", row.educatorCode, "topics", row.slug),
        {
          status: "promoted",
          promotedAt: serverTimestamp(),
          promotedCount: incoming.length,
        },
        { merge: true }
      );

      setSubmissions((prev) => prev.filter((r) => r.path !== row.path));
      setMessage(
        `Added ${incoming.length} question${incoming.length === 1 ? "" : "s"} to "${titleCase(row.slug)}".`
      );
    } catch (err) {
      console.error("[AdminQuizReview] promote failed:", err);
      setError("Couldn't promote that submission.");
    } finally {
      setBusySlug("");
    }
  };

  // -------------------------------------------------------------------------

  return (
    <div style={s.page}>
      <div style={s.shell}>
        <header style={s.head}>
          <div>
            <p style={s.eyebrow}>Admin</p>
            <h1 style={s.h1}>Educator question review</h1>
            <p style={s.sub}>
              Questions teachers wrote and chose to share. Promoting adds them
              to the global library served to every school.
            </p>
          </div>
          <button style={s.ghost} onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </header>

        {message && <div style={s.success}>{message}</div>}
        {error && <div style={s.error}>{error}</div>}

        {loading ? (
          <p style={s.muted}>Loading submissions…</p>
        ) : submissions.length === 0 ? (
          <div style={s.empty}>
            <h2 style={s.emptyTitle}>Nothing waiting</h2>
            <p style={s.emptyBody}>
              Submitted questions appear here once an educator chooses to share
              them.
            </p>
          </div>
        ) : (
          submissions.map((row) => (
            <SubmissionCard
              key={row.path}
              row={row}
              busy={busySlug === row.path}
              onPromote={() => promote(row)}
              onReject={() => setStatus(row, "rejected")}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SubmissionCard({ row, busy, onPromote, onReject }) {
  return (
    <section style={s.card}>
      <div style={s.cardHead}>
        <div>
          <h2 style={s.cardTitle}>{titleCase(row.slug)}</h2>
          <p style={s.cardMeta}>
            {row.educatorName || "Educator"} · code {row.educatorCode}
            {row.state ? ` · ${String(row.state).toUpperCase()}` : ""}
            {row.grade ? ` · ${row.grade}` : ""} ·{" "}
            {(row.questions || []).length} question
            {(row.questions || []).length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      {row.stats && row.stats.totalAnswers > 0 ? (
        <p style={s.evidence}>
          <strong>{row.stats.totalAnswers}</strong> student answers recorded ·{" "}
          <strong>{row.stats.percentCorrect}%</strong> correct overall
          {row.stats.percentCorrect < 25 && (
            <span style={s.warn}>
              {" "}
              — unusually low. Read carefully: brilliant, or broken.
            </span>
          )}
          {row.stats.percentCorrect > 92 && (
            <span style={s.note}> — very high. Possibly too easy.</span>
          )}
        </p>
      ) : (
        <p style={s.evidenceMuted}>
          No student answers recorded yet — no performance evidence to go on.
        </p>
      )}

      <ol style={s.qList}>
        {(row.questions || []).map((q, i) => (
          <li key={i} style={s.qItem}>
            <p style={s.qText}>{q.question}</p>
            <ul style={s.aList}>
              {(q.answers || []).map((a, j) => (
                <li key={j} style={a.correct ? s.aCorrect : s.aWrong}>
                  {a.correct ? "✓ " : "· "}
                  {a.text}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      <div style={s.actions}>
        <button
          style={{ ...s.promote, opacity: busy ? 0.6 : 1 }}
          disabled={busy}
          onClick={onPromote}
        >
          {busy ? "Working…" : "Add to global library"}
        </button>
        <button
          style={{ ...s.reject, opacity: busy ? 0.6 : 1 }}
          disabled={busy}
          onClick={onReject}
        >
          Decline
        </button>
      </div>
      <p style={s.declineNote}>
        Declining only removes it from this queue. The educator's own class
        keeps using their questions.
      </p>
    </section>
  );
}

/**
 * Pulls answer detail for this topic from the submitting educator's own
 * classroom mirror.
 *
 * Scoped to that educator because that's who the questions were served to —
 * a global scan would mix in results from the canonical version of the topic
 * and tell you nothing about the submission.
 */
async function gatherStats(db, educatorCode, slug) {
  const snap = await getDocs(
    query(
      collectionGroup(db, "students"),
      where("educatorCode", "==", String(educatorCode))
    )
  );

  let totalAnswers = 0;
  let correct = 0;

  snap.forEach((d) => {
    const topic = d.data()?.slugs?.[slug];
    const answers = topic?.game?.latestDetail?.answers;
    if (!Array.isArray(answers)) return;
    answers.forEach((a) => {
      totalAnswers += 1;
      if (a.correct || a.isCorrect) correct += 1;
    });
  });

  return {
    totalAnswers,
    correct,
    percentCorrect: totalAnswers
      ? Math.round((correct / totalAnswers) * 100)
      : 0,
  };
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    padding: "32px 20px 72px",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    color: "#0f172a",
  },
  shell: { maxWidth: 860, margin: "0 auto" },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 22,
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  h1: { margin: "4px 0 6px", fontSize: 30, fontWeight: 800 },
  sub: { margin: 0, color: "#475569", fontSize: 15, maxWidth: 520 },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 22,
    marginBottom: 16,
  },
  cardHead: { marginBottom: 10 },
  cardTitle: { margin: 0, fontSize: 20, fontWeight: 700 },
  cardMeta: { margin: "3px 0 0", color: "#64748b", fontSize: 13.5 },
  evidence: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13.5,
    color: "#334155",
    margin: "0 0 14px",
  },
  evidenceMuted: {
    fontSize: 13.5,
    color: "#94a3b8",
    fontStyle: "italic",
    margin: "0 0 14px",
  },
  warn: { color: "#b45309", fontWeight: 600 },
  note: { color: "#64748b" },
  qList: { paddingLeft: 20, margin: "0 0 16px" },
  qItem: { marginBottom: 14 },
  qText: { margin: "0 0 5px", fontWeight: 600, fontSize: 15 },
  aList: { listStyle: "none", padding: 0, margin: 0, fontSize: 14 },
  aCorrect: { color: "#15803d", fontWeight: 600 },
  aWrong: { color: "#64748b" },
  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  promote: {
    background: "#15803d",
    color: "#fff",
    border: "none",
    borderRadius: 9,
    padding: "11px 20px",
    fontWeight: 700,
    cursor: "pointer",
  },
  reject: {
    background: "#fff",
    color: "#b91c1c",
    border: "1px solid #fecaca",
    borderRadius: 9,
    padding: "11px 20px",
    fontWeight: 700,
    cursor: "pointer",
  },
  declineNote: { fontSize: 12.5, color: "#94a3b8", margin: "10px 0 0" },
  ghost: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 18px",
    fontWeight: 600,
    cursor: "pointer",
  },
  empty: {
    background: "#fff",
    border: "1px dashed #e2e8f0",
    borderRadius: 12,
    padding: "44px 28px",
    textAlign: "center",
  },
  emptyTitle: { margin: "0 0 8px", fontSize: 20, fontWeight: 700 },
  emptyBody: { margin: 0, color: "#64748b" },
  success: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    color: "#166534",
    borderRadius: 8,
    padding: "11px 14px",
    fontSize: 14,
    marginBottom: 14,
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: 8,
    padding: "11px 14px",
    fontSize: 14,
    marginBottom: 14,
  },
  muted: { color: "#64748b" },
};
